import React from 'react'
import { Link } from 'react-router-dom'
import { SmartTooltip } from '../../../../../components/SmartTooltip/SmartTooltip'
import { KTIcon } from '../../../../../_metronic/helpers'
import { matchingActiveDepositsUrl, matchingTicketCardUrl } from '../../../../common/MatchingTicketLink'
import { MatchingPayoutHaltHold, MatchingPayoutTicket } from '../../core/_models'
import { matchingActivePaymentsConfig } from '../payments-active/config'
import {
  HALT_UNDETERMINED_LABEL,
  haltHoldNeed,
  haltHoldReasonLabel,
  hasHaltExplanation,
} from '../../core/haltHolds'

type Props = {
  ticket: MatchingPayoutTicket
  /** The matching status text the hint is attached to. */
  label: string
}

const cell: React.CSSProperties = {
  padding: '4px 12px 2px 0',
  color: 'var(--bs-body-color)',
  verticalAlign: 'top',
}
const head: React.CSSProperties = { ...cell, paddingBottom: 4, fontWeight: 600, textAlign: 'left' }

const activeListStatuses: ReadonlyArray<string> = matchingActivePaymentsConfig.relevantStatusList

/**
 * The active deposits list filters by its own statuses, so a deposit outside them would open an
 * empty list — that one goes to its matching card instead.
 */
const holdDepositUrl = (hold: MatchingPayoutHaltHold): string =>
  hold.depositStatus && !activeListStatuses.includes(hold.depositStatus)
    ? matchingTicketCardUrl(hold.depositToken)
    : matchingActiveDepositsUrl(hold.depositToken)

/**
 * A halted payout that has not closed yet: which deposits keep it waiting, why, and what has to
 * happen. Links open the active deposits list filtered by the deposit in a new tab; the tooltip
 * stays open while the pointer is on it, so they are clickable.
 */
export const HaltHoldsHint: React.FC<Props> = ({ ticket, label }) => {
  if (!hasHaltExplanation(ticket)) return <span>{label}</span>

  const holds = ticket.haltHolds ?? []

  const card = (
    <div
      style={{
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)',
        border: '1px solid var(--bs-border-color)',
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,.15)',
        padding: '8px 12px',
        minWidth: 520,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
        Почему выплата ещё не закрыта
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <th style={head}>Пополнение</th>
            <th style={head}>Причина</th>
            <th style={head}>Что нужно</th>
          </tr>
        </thead>
        <tbody>
          {holds.map((hold) => (
            <tr key={hold.depositToken} style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                <Link
                  to={holdDepositUrl(hold)}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary'
                  onClick={(e) => e.stopPropagation()}
                >
                  {hold.depositToken}
                </Link>
              </td>
              <td style={cell}>{haltHoldReasonLabel(hold.reason)}</td>
              <td style={cell}>{haltHoldNeed(hold)}</td>
            </tr>
          ))}
          {holds.length === 0 && (
            <tr>
              <td style={cell}>—</td>
              <td style={cell}>{HALT_UNDETERMINED_LABEL}</td>
              <td style={cell}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )

  return (
    <SmartTooltip content={card} bare>
      <span className='d-inline-flex align-items-center gap-1' style={{ cursor: 'default' }}>
        {label}
        <span data-testid='halt-holds-icon' className='d-inline-flex'>
          <KTIcon iconName='information-5' className='fs-6 text-warning' />
        </span>
      </span>
    </SmartTooltip>
  )
}

export default HaltHoldsHint
